/**
 * Use Case Selector Component
 *
 * Dropdown for selecting a use case under the chosen facility.
 *
 * Central-MU: when source/target serviceId is set, fetches via MU's
 * /api/instances/{id}/use-cases (routed through LeucineInstanceClient). Falls back to the
 * legacy useUseCases(client, facilityId) which talks to streem-backend directly from the
 * browser when no serviceId is set.
 */

import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useUseCases, UseCase } from "../../../lib/api";
import { useInstanceUseCases } from "../../../lib/api-central-mu";

interface UseCaseSelectorProps {
  className?: string;
  isTarget?: boolean;
  label?: string;
  client?: any;
  facilityId?: string | null;
}

export function UseCaseSelector({
  className,
  isTarget = false,
  label = "Use Case",
  client,
  facilityId
}: UseCaseSelectorProps) {
  const {
    selectedFacilityId,
    selectedUseCaseId,
    selectedServiceId,
    setSelectedUseCaseId,
    setSelectedUseCaseName,
    selectedClient,
    targetFacilityId,
    targetUseCaseId,
    targetServiceId,
    setTargetUseCaseId,
    setTargetUseCaseName,
    targetClient,
  } = useWorkflowStore();

  const currentClient = client || (isTarget ? targetClient : selectedClient);
  const currentFacilityId = facilityId || (isTarget ? targetFacilityId : selectedFacilityId);
  const currentUseCaseId = isTarget ? targetUseCaseId : selectedUseCaseId;
  const setUseCaseId = isTarget ? setTargetUseCaseId : setSelectedUseCaseId;
  const setUseCaseName = isTarget ? setTargetUseCaseName : setSelectedUseCaseName;

  const serviceIdStr = isTarget ? targetServiceId : selectedServiceId;
  const serviceIdNumber = serviceIdStr ? Number(serviceIdStr) : null;
  const useCentralMU = !!serviceIdNumber;

  // Lazy-load: don't fetch use cases when a facility is selected. Only fetch the
  // first time the user opens the dropdown (onClick), then keep the result cached.
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  const centralMUQuery = useInstanceUseCases(
    useCentralMU ? serviceIdNumber : null,
    useCentralMU ? currentFacilityId : null,
    hasOpened,
  );
  const legacyQuery = useUseCases(
    useCentralMU ? null : currentClient,
    useCentralMU ? null : currentFacilityId,
    hasOpened,
  );

  const useCases: UseCase[] = useCentralMU
    ? ((centralMUQuery.data as unknown as UseCase[]) || [])
    : (legacyQuery.data || []);
  const isLoading = useCentralMU ? centralMUQuery.isLoading : legacyQuery.isLoading;
  const error = useCentralMU ? centralMUQuery.error : legacyQuery.error;

  // Reset selection and lazy-load gate when the facility/instance changes.
  useEffect(() => {
    setUseCaseId(null);
    setUseCaseName(null);
    setHasOpened(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFacilityId, currentClient, serviceIdNumber]);

  // Trigger the fetch only when the dropdown is first opened.
  const handleOpenChange = (open: boolean) => {
    setDropdownOpen(open);
    if (open && !hasOpened && currentFacilityId) {
      setHasOpened(true);
    }
  };

  const handleUseCaseChange = (value: string) => {
    setUseCaseId(value);
    setUseCaseName(useCases.find((u: UseCase) => u.id === value)?.name ?? null);
  };

  const hasSource = useCentralMU || !!currentClient;
  const isDisabled = !currentFacilityId || !hasSource;

  const getPlaceholderText = () => {
    if (!hasSource) return isTarget ? "Select a target instance first" : "Select a source instance first";
    if (!currentFacilityId) return isTarget ? "Select a facility first" : "Select a source facility first";
    if (!hasOpened) return "Click to load use cases";
    return isTarget ? "Select use case" : "Select source use case";
  };

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={isTarget ? "target-use-case" : "use-case"}>{label}</Label>
        <Select
          value={currentUseCaseId || ""}
          onValueChange={handleUseCaseChange}
          disabled={isDisabled}
          open={dropdownOpen}
          onOpenChange={handleOpenChange}
        >
          <SelectTrigger id={isTarget ? "target-use-case" : "use-case"}>
            <SelectValue placeholder={getPlaceholderText()} />
          </SelectTrigger>
          <SelectContent>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading use cases...
              </SelectItem>
            ) : error ? (
              <SelectItem value="error" disabled>
                Error: {error instanceof Error ? error.message : "Failed to load use cases"}
              </SelectItem>
            ) : useCases.length === 0 ? (
              <SelectItem value="none" disabled>
                No use cases available
              </SelectItem>
            ) : (
              useCases.map((useCase: UseCase) => (
                <SelectItem key={useCase.id} value={useCase.id}>
                  {useCase.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
